/**
 * Tabs component functionality for SFCC Page Designer
 * Handles tab switching, keyboard navigation, deep-linking, and accessibility
 */

var $ = require('jquery');

/**
 * Tab component constructor
 * @param {HTMLElement} element - The tabs container element
 * @constructor
 */
function Tabs(element) {
    this.element = element;
    this.$element = $(element);
    this.componentId = this.$element.data('component-id');
    this.initialIndex = parseInt(this.$element.data('initial-index'), 10) || 0;
    this.equalizeHeights = this.$element.data('equalize-heights') === true;
    this.tabWidthMode = this.$element.data('tab-width-mode') || 'fill';
    this.mobileLayoutMode = this.$element.data('mobile-layout-mode') || 'fixed';
    
    this.$tablist = this.$element.find('.pd-tabs__tablist');
    this.$tabs = this.$element.find('.pd-tabs__tab');
    this.$panels = this.$element.find('.pd-tab-panel');
    
    this.currentIndex = this.initialIndex;
    
    this.init();
}

Tabs.prototype = {
    /**
     * Initialize the tabs component
     */
    init: function () {
        if (this.$tabs.length === 0 || this.$panels.length === 0) {
            return;
        }
        
        this.setupEventHandlers();
        this.setupInitialState();
        this.handleDeepLinking();
        
        if (this.equalizeHeights) {
            this.equalizeTabHeights();
        }
    },
    
    /**
     * Set up event handlers for tabs
     */
    setupEventHandlers: function () {
        var self = this;
        
        // Click handlers for tabs
        this.$tabs.on('click.tabs', function (event) {
            event.preventDefault();
            var $tab = $(this);
            var index = parseInt($tab.data('tab-index'), 10);
            
            if (!$tab.hasClass('pd-tabs__tab--disabled')) {
                self.hasUserInteraction = true;
                self.activateTab(index);
            }
        });
        
        // Keyboard navigation for tabs
        this.$tabs.on('keydown.tabs', function (event) {
            self.handleKeydown(event);
        });
        
        // Handle window resize for equalized heights
        if (this.equalizeHeights) {
            $(window).on('resize.tabs-' + this.componentId, function () {
                clearTimeout(self.resizeTimeout);
                self.resizeTimeout = setTimeout(function () {
                    self.equalizeTabHeights();
                }, 250);
            });
        }
        
        // Handle hash change for deep linking
        $(window).on('hashchange.tabs-' + this.componentId, function () {
            self.handleDeepLinking();
        });
    },
    
    /**
     * Set up initial tab state
     */
    setupInitialState: function () {
        // Find first non-disabled tab if initial index is disabled
        var targetIndex = this.initialIndex;
        var $targetTab = this.$tabs.eq(targetIndex);
        
        if ($targetTab.length === 0 || $targetTab.hasClass('pd-tabs__tab--disabled')) {
            targetIndex = this.findFirstEnabledTab();
        }
        
        // Ensure we have valid tabs and panels
        if (this.$tabs.length > 0 && this.$panels.length > 0) {
            this.activateTab(targetIndex);
        }
    },
    
    /**
     * Find the first enabled tab index
     * @returns {number} - Index of first enabled tab
     */
    findFirstEnabledTab: function () {
        for (var i = 0; i < this.$tabs.length; i++) {
            if (!this.$tabs.eq(i).hasClass('pd-tabs__tab--disabled')) {
                return i;
            }
        }
        return 0;
    },
    
    /**
     * Activate a specific tab by index
     * @param {number} index - Tab index to activate
     */
    activateTab: function (index) {
        if (index < 0 || index >= this.$tabs.length) {
            return;
        }
        
        var $targetTab = this.$tabs.eq(index);
        var $targetPanel = this.$panels.eq(index);
        
        if ($targetTab.hasClass('pd-tabs__tab--disabled') || !$targetPanel.length) {
            return;
        }
        
        this.currentIndex = index;
        
        // Update tab states
        this.$tabs.removeClass('pd-tabs__tab--active')
            .attr('aria-selected', 'false')
            .attr('tabindex', '-1');
        
        $targetTab.addClass('pd-tabs__tab--active')
            .attr('aria-selected', 'true')
            .attr('tabindex', '0');
        
        // Update panel states
        this.$panels.removeClass('pd-tab-panel--active')
            .attr('aria-hidden', 'true');
        
        $targetPanel.addClass('pd-tab-panel--active')
            .attr('aria-hidden', 'false');
        
        // Update URL hash if panel has an ID
        var panelId = $targetPanel.attr('id');
        if (panelId && this.shouldUpdateHash()) {
            this.updateHash(panelId);
        }
        
        // Trigger custom event
        this.$element.trigger('tabactivated.tabs', {
            index: index,
            tab: $targetTab[0],
            panel: $targetPanel[0]
        });
    },
    
    /**
     * Handle keyboard navigation
     * @param {Event} event - Keyboard event
     */
    handleKeydown: function (event) {
        var $target = $(event.target);
        var currentIndex = parseInt($target.data('tab-index'), 10);
        var newIndex = currentIndex;
        
        switch (event.which) {
            case 37: // Left arrow
                newIndex = this.getPreviousEnabledTab(currentIndex);
                break;
            case 39: // Right arrow
                newIndex = this.getNextEnabledTab(currentIndex);
                break;
            case 36: // Home
                newIndex = this.findFirstEnabledTab();
                break;
            case 35: // End
                newIndex = this.findLastEnabledTab();
                break;
            case 13: // Enter
            case 32: // Space
                event.preventDefault();
                this.activateTab(currentIndex);
                return;
            default:
                return; // Allow default behavior for other keys
        }
        
        if (newIndex !== currentIndex) {
            event.preventDefault();
            this.focusTab(newIndex);
        }
    },
    
    /**
     * Get the previous enabled tab index
     * @param {number} currentIndex - Current tab index
     * @returns {number} - Previous enabled tab index
     */
    getPreviousEnabledTab: function (currentIndex) {
        for (var i = currentIndex - 1; i >= 0; i--) {
            if (!this.$tabs.eq(i).hasClass('pd-tabs__tab--disabled')) {
                return i;
            }
        }
        // Wrap around to last enabled tab
        return this.findLastEnabledTab();
    },
    
    /**
     * Get the next enabled tab index
     * @param {number} currentIndex - Current tab index
     * @returns {number} - Next enabled tab index
     */
    getNextEnabledTab: function (currentIndex) {
        for (var i = currentIndex + 1; i < this.$tabs.length; i++) {
            if (!this.$tabs.eq(i).hasClass('pd-tabs__tab--disabled')) {
                return i;
            }
        }
        // Wrap around to first enabled tab
        return this.findFirstEnabledTab();
    },
    
    /**
     * Find the last enabled tab index
     * @returns {number} - Index of last enabled tab
     */
    findLastEnabledTab: function () {
        for (var i = this.$tabs.length - 1; i >= 0; i--) {
            if (!this.$tabs.eq(i).hasClass('pd-tabs__tab--disabled')) {
                return i;
            }
        }
        return 0;
    },
    
    /**
     * Focus a specific tab
     * @param {number} index - Tab index to focus
     */
    focusTab: function (index) {
        this.$tabs.eq(index).focus();
    },
    
    /**
     * Handle deep linking via URL hash
     */
    handleDeepLinking: function () {
        var hash = window.location.hash;
        if (!hash || hash.length <= 1) {
            return;
        }
        
        var targetId = hash.substring(1); // Remove # from hash
        var $targetPanel = this.$element.find('#' + targetId);
        
        if ($targetPanel.length && $targetPanel.hasClass('pd-tab-panel')) {
            var panelIndex = this.$panels.index($targetPanel);
            if (panelIndex >= 0) {
                this.activateTab(panelIndex);
            }
        }
    },
    
    /**
     * Update URL hash
     * @param {string} panelId - Panel ID to set as hash
     */
    updateHash: function (panelId) {
        if (window.history && window.history.replaceState) {
            window.history.replaceState(null, null, '#' + panelId);
        } else {
            window.location.hash = panelId;
        }
    },
    
    /**
     * Check if hash should be updated (avoid updating on initial load)
     * @returns {boolean} - Whether to update hash
     */
    shouldUpdateHash: function () {
        // Don't update hash if this is the initial setup
        return this.hasUserInteraction;
    },
    
    /**
     * Equalize the heights of all tab panels
     */
    equalizeTabHeights: function () {
        if (!this.equalizeHeights || this.$panels.length === 0) {
            return;
        }
        
        // Reset heights to auto to get natural heights
        this.$panels.css('height', 'auto');
        
        // Find the tallest panel
        var maxHeight = 0;
        this.$panels.each(function () {
            var height = $(this).outerHeight();
            if (height > maxHeight) {
                maxHeight = height;
            }
        });
        
        // Set all panels to the maximum height
        if (maxHeight > 0) {
            this.$panels.css('height', maxHeight + 'px');
        }
    },
    
    /**
     * Destroy the tabs component
     */
    destroy: function () {
        // Remove event handlers
        this.$tabs.off('.tabs');
        $(window).off('.tabs-' + this.componentId);
        
        // Clear timeouts
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        
        // Reset heights if equalized
        if (this.equalizeHeights) {
            this.$panels.css('height', '');
        }
        
        // Remove references
        this.$element.removeData('tabs');
    }
};

/**
 * jQuery plugin for tabs
 * @param {Object|string} options - Configuration options or method name
 * @returns {jQuery} - jQuery object for chaining
 */
$.fn.tabs = function (options) {
    return this.each(function () {
        var $this = $(this);
        var instance = $this.data('tabs');
        
        if (!instance) {
            instance = new Tabs(this);
            $this.data('tabs', instance);
        }
        
        if (typeof options === 'string' && typeof instance[options] === 'function') {
            instance[options]();
        }
    });
};

/**
 * Initialize all tabs components on the page
 */
function initializeTabs() {
    $('.pd-tabs').not('[data-tabs-initialized]').each(function () {
        $(this).attr('data-tabs-initialized', 'true').tabs();
    });
}

// Auto-initialize tabs components when DOM is ready
$(document).ready(function () {
    initializeTabs();
});

// For SFCC Page Designer - initialize on window load as well
$(window).on('load', function () {
    setTimeout(initializeTabs, 100);
});

// Watch for dynamically added content (Page Designer preview mode)
var observer;
if (window.MutationObserver) {
    observer = new MutationObserver(function (mutations) {
        var shouldInitialize = false;
        mutations.forEach(function (mutation) {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                for (var i = 0; i < mutation.addedNodes.length; i++) {
                    var node = mutation.addedNodes[i];
                    if (node.nodeType === 1) { // Element node
                        if ($(node).hasClass('pd-tabs') || $(node).find('.pd-tabs').length > 0) {
                            shouldInitialize = true;
                            break;
                        }
                    }
                }
            }
        });
        
        if (shouldInitialize) {
            setTimeout(initializeTabs, 200);
        }
    });
    
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}

module.exports = Tabs;